// TODO(yandex-metrica/task-21): wire order_paid once pay-success receives real
// order context (order_id + revenue_kop) from route state or query params.
// When real context is available, fire in a useEffect with [order_id, revenue_kop] deps:
//   import { useEffect } from "react";
//   import { EVENTS, trackEvent } from "@/lib/analytics";
//   useEffect(() => {
//     trackEvent(EVENTS.ORDER_PAID, {
//       order_id: orderId,
//       revenue_kop: revenueKop,
//       currency: "RUB",
//     });
//   }, [orderId, revenueKop]);
import { useOrderDetail } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";

// Placeholder shown while real order detail loads — NEVER a fake order number (#259).
const PLACEHOLDER = "—";

/**
 * /pay-success
 *
 * Design: _design_reference/mini-pay-success.html
 * - Full-page radial-gradient green background
 * - Bear mascot SVG illustration (inline, from design)
 * - Title + subtitle
 * - Info card: order_no · paid amount · items count · buyer ETA
 * - CTA → /orders
 * - On mount: invalidate ["user","info"] so header/wallet balance refreshes (#pay_success_wallet_refresh)
 */
export function PaySuccessPage() {
  const { t } = useTranslation("pay");
  const authToken = useAuthToken();
  const qc = useQueryClient();

  // Read orderNo from URL search params (?orderNo=KK-xxx or ?order_no=KK-xxx)
  const searchStr = useRouterState({ select: (s) => s.location.searchStr ?? "" });
  const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
  const orderNo = params.get("orderNo") ?? params.get("order_no") ?? undefined;
  const amountParam = params.get("amount") ?? undefined;

  // 支付成功后余额已变动，invalidate userInfo 缓存让 header / 钱包页拿到最新余额
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ["user", "info"] });
  }, [qc]);

  // Fetch real order detail for accurate paid amount, item count, shop count
  const detailQ = useOrderDetail(authToken, orderNo ?? "");
  const detail = detailQ.data;
  const loading = !!orderNo && !!authToken && detailQ.isLoading;

  const { itemCount, shopCount } = useMemo(() => {
    if (!detail) return { itemCount: 0, shopCount: 0 };
    const count = detail.items.reduce((acc, it) => acc + (it.quantity ?? 0), 0);
    const shops = new Set(
      detail.items
        .map((it) => it.shopCode ?? it.shopName)
        .filter((s): s is string => !!s && s.length > 0),
    );
    return { itemCount: count, shopCount: shops.size };
  }, [detail]);

  const displayOrderNo = orderNo ?? PLACEHOLDER;
  const displayAmount = detail
    ? formatPrice(detail.totalPayAmountRubKopecks, "ru-RU", "RUB")
    : (amountParam ?? PLACEHOLDER);
  const displayItems =
    loading || !detail
      ? PLACEHOLDER
      : t("success.meta_items_value", { count: itemCount, shops: shopCount });

  return (
    <TgPage tabbar={false}>
      {/* Full-page gradient background per mini-pay-success.html design */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 40% at 50% 30%,rgba(11,168,136,.2),transparent 60%),linear-gradient(180deg,#E0F7F1,#B8E8DD)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", flex: 1 }}>
      <TgHeader title={t("success.head_title")} back />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          textAlign: "center",
        }}
      >
        {/* Bear mascot illustration — verbatim SVG from mini-pay-success.html */}
        {/* biome-ignore lint/style/useSelfClosingElements: inline SVG */}
        <svg
          viewBox="0 0 400 480"
          style={{ width: 180, filter: "drop-shadow(0 10px 20px rgba(15,31,26,.18))" }}
          aria-hidden="true"
        >
          <ellipse cx="200" cy="455" rx="130" ry="14" fill="rgba(15,31,26,.18)" />
          <ellipse cx="155" cy="432" rx="34" ry="24" fill="#C8A678" />
          <ellipse cx="245" cy="432" rx="34" ry="24" fill="#C8A678" />
          <ellipse cx="200" cy="345" rx="124" ry="112" fill="#C8A678" />
          <ellipse cx="200" cy="345" rx="106" ry="96" fill="#D4B58A" />
          <ellipse cx="200" cy="375" rx="64" ry="58" fill="#F0DBB8" />
          <ellipse cx="124" cy="316" rx="32" ry="50" fill="#C8A678" transform="rotate(-22 124 316)" />
          <ellipse cx="276" cy="316" rx="32" ry="50" fill="#C8A678" transform="rotate(22 276 316)" />
          <rect x="138" y="298" width="124" height="92" rx="14" fill="#FF6B4A" />
          <rect x="194" y="298" width="12" height="92" fill="#fff" />
          <rect x="138" y="338" width="124" height="12" fill="#fff" />
          <ellipse cx="186" cy="295" rx="11" ry="8" fill="#FFD23F" />
          <ellipse cx="214" cy="295" rx="11" ry="8" fill="#FFD23F" />
          <circle cx="132" cy="100" r="36" fill="#C8A678" />
          <circle cx="268" cy="100" r="36" fill="#C8A678" />
          <circle cx="132" cy="102" r="20" fill="#FF8FA8" />
          <circle cx="268" cy="102" r="20" fill="#FF8FA8" />
          <circle cx="200" cy="180" r="104" fill="#C8A678" />
          <circle cx="200" cy="180" r="94" fill="#D4B58A" />
          <rect x="108" y="138" width="184" height="12" rx="6" fill="#4A3829" />
          <circle cx="148" cy="138" r="22" fill="#4A3829" />
          <circle cx="148" cy="138" r="17" fill="#fff" />
          <circle cx="148" cy="138" r="14" fill="#7DD3C0" />
          <circle cx="252" cy="138" r="22" fill="#4A3829" />
          <circle cx="252" cy="138" r="17" fill="#fff" />
          <circle cx="252" cy="138" r="14" fill="#7DD3C0" />
          <ellipse cx="200" cy="222" rx="52" ry="38" fill="#F0DBB8" />
          <circle cx="158" cy="184" r="10" fill="#0F1F1A" />
          <circle cx="242" cy="184" r="10" fill="#0F1F1A" />
          <ellipse cx="200" cy="208" rx="15" ry="11" fill="#0F1F1A" />
          <path
            d="M 200 220 Q 200 234 186 234 Q 180 234 180 228"
            stroke="#0F1F1A"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M 200 220 Q 200 234 214 234 Q 220 234 220 228"
            stroke="#0F1F1A"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M 130 260 Q 200 280 270 260 L 274 282 Q 200 302 126 282 Z"
            fill="#E54A2A"
          />
        </svg>

        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 22,
            fontWeight: 900,
            margin: "14px 0 8px",
            letterSpacing: "-0.01em",
          }}
        >
          {t("mini.success_title")}
        </h1>
        <p
          style={{
            fontSize: 13,
            color: "var(--color-text-muted)",
            maxWidth: 280,
            lineHeight: 1.55,
            margin: "0 0 22px",
          }}
        >
          {t("mini.success_hint")}
        </p>

        {/* Info card */}
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: "14px 18px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            width: "100%",
            maxWidth: 320,
            textAlign: "left",
          }}
        >
          <InfoCell
            label={t("success.meta_order_no")}
            value={displayOrderNo}
            mono
          />
          <InfoCell
            label={t("success.meta_paid")}
            value={displayAmount}
            accent
          />
          <InfoCell
            label={t("success.meta_items")}
            value={displayItems}
          />
          <InfoCell
            label={t("success.meta_eta")}
            value={t("success.meta_eta_value")}
            primary
          />
        </div>
      </div>

      {/* Bottom CTA — matches design `.tg-main` style */}
      <Link
        to={"/orders" as never}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 16px 16px",
          padding: "14px 24px",
          background: "var(--color-cta)",
          color: "#fff",
          borderRadius: 14,
          fontSize: 15,
          fontWeight: 800,
          textDecoration: "none",
        }}
      >
        {t("success.cta_view_order")}
      </Link>
      </div>
    </TgPage>
  );
}

function InfoCell({
  label,
  value,
  mono,
  accent,
  primary,
}: {
  label: string;
  value: string;
  mono?: boolean;
  accent?: boolean;
  primary?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          color: "var(--color-text-muted)",
          fontFamily: "monospace",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: mono ? "monospace" : "-apple-system,system-ui,sans-serif",
          fontSize: mono ? 11 : 13,
          fontWeight: 800,
          marginTop: 2,
          ...(accent ? { color: "var(--color-price)" } : {}),
          ...(primary ? { color: "var(--color-primary-fill)" } : {}),
        }}
      >
        {value}
      </div>
    </div>
  );
}
